import { queryFeatureService } from '../arcgis/jsapi/js-api-util';

// All functions return distinct (unique) values.

// Utility function for use with useAsync hook.
export const queryNDS = async ({
    url,
    fnc,
    ...rest
}: {
    url: string;
    fnc: Function;
}): Promise<any> => await fnc({ url, ...rest });

// Return the attributes only.
export const getAttributes = async ({
    url,
    ...rest
}: {
    url: string;
}): Promise<any> => {
    const response = await queryFeatureService({ url, ...rest });
    return response.features.map((f: any) => f.attributes);
};

export const getGeo = async ({
    url,
    id,
    ...rest
}: {
    url: string;
    id: string;
}): Promise<any> => {
    const geos = await getGeos({ url, ids: [id], ...rest });
    return geos[0];
};

export const getGeos = async ({
    url,
    ids = [],
    outFields = 'ID, Item_Order, Item_Type, Name, Service_Url, Short_Name',
    orderByFields = 'Item_Order ASC',
    returnDistinctValues = true,
    ...rest
}: {
    url: string;
    ids?: string[];
    outFields?: string;
    orderByFields?: string;
    returnDistinctValues?: boolean;
}): Promise<any> =>
    await getAttributes({
        url,
        where:
            ids.length > 0
                ? `Item_Type='Geo' AND ID IN ('${ids.join("','")}')`
                : `Item_Type='Geo'`,
        outFields,
        orderByFields,
        returnDistinctValues,
        ...rest
    });

export const getGeosForIndicator = async ({
    url,
    indicatorId,
    ...rest
}: {
    url: string;
    indicatorId: string;
}): Promise<any> => {
    const inds = await getIndicators({
        url,
        ids: [indicatorId],
        outFields: '*'
    });
    const geoIds = inds.map((ind: any) => ind.Geo_ID);
    return await getGeos({ url, ids: geoIds, ...rest });
};

export const getIndicator = async ({
    url,
    id,
    ...rest
}: {
    url: string;
    id: string;
}): Promise<any> => {
    const inds = await getIndicators({ url, ids: [id], ...rest });
    return inds[0];
};

export const getIndicators = async ({
    url,
    ids = [],
    outFields = 'Data_Type, Denominator, ID, Item_Order, Item_Type, Name, Numerator, Scale_Factor, Short_Name, Theme_ID',
    orderByFields = 'Item_Order ASC',
    returnDistinctValues = true,
    ...rest
}: {
    url: string;
    ids?: string[];
    outFields?: string;
    orderByFields?: string;
    returnDistinctValues?: boolean;
}): Promise<any> =>
    await getAttributes({
        url,
        where:
            ids.length > 0
                ? `Item_Type='Indicator' AND ID IN ('${ids.join("','")}')`
                : `Item_Type='Indicator'`,
        outFields,
        orderByFields,
        returnDistinctValues,
        ...rest
    });

const removeDuplicateItems = (keyFn: Function, array: any[]) => {
    let mySet = new Set();
    return array.filter((x) => {
        let key = keyFn(x),
            isNew = !mySet.has(key);
        if (isNew) mySet.add(key);
        return isNew;
    });
};

export const searchIndicators = async ({
    url,
    searchTerm = '',
    themes = [],
    outFields = 'Data_Type, Denominator, ID, Item_Order, Item_Type, Name, Numerator, Scale_Factor, Short_Name, Theme_ID',
    orderByFields = 'Name ASC',
    returnDistinctValues = true,
    ...rest
}: {
    url: string;
    searchTerm?: string;
    themes?: string[];
    outFields?: string;
    orderByFields?: string;
    returnDistinctValues?: boolean;
}): Promise<any> => {
    const str = searchTerm.trim().toLowerCase();
    const arrStr = str.match(/\S+/g) || [];
    const arrStrTokens = arrStr.map(
        (tkn: any) => `Name LIKE '%[^a-z0-9]${tkn}[^a-z0-9]%'`
    );
    const arrPartialStrTokens = arrStr.map(
        (tkn: any) => `Name LIKE '%${tkn}%'`
    );

    // Full string.
    let inds = await getAttributes({
        url,
        where: `Item_Type='Indicator' AND Name LIKE '%[^a-z0-9]${str}[^a-z0-9]%'`,
        outFields,
        orderByFields,
        exceedMaxRecordCount: false,
        returnDistinctValues,
        ...rest
    });

    // Whole word AND.
    if (inds.length === 0) {
        const indsAnd = await getAttributes({
            url,
            where: `Item_Type='Indicator' AND (${arrStrTokens.join(' AND ')})`,
            outFields,
            orderByFields,
            returnDistinctValues,
            exceedMaxRecordCount: false,
            ...rest
        });
        inds = removeDuplicateItems(
            (oIndicator: any) => oIndicator.ID,
            [...inds, ...indsAnd]
        );
    }

    // Whole word OR.
    if (inds.length === 0) {
        const indsOr = await getAttributes({
            url,
            where: `Item_Type='Indicator' AND (${arrStrTokens.join(' OR ')})`,
            outFields,
            orderByFields,
            returnDistinctValues,
            exceedMaxRecordCount: false,
            ...rest
        });
        inds = removeDuplicateItems(
            (oIndicator: any) => oIndicator.ID,
            [...inds, ...indsOr]
        );
    }

    // Partial word OR.
    if (inds.length === 0) {
        const indsOr = await getAttributes({
            url,
            where: `Item_Type='Indicator' AND (${arrPartialStrTokens.join(
                ' OR '
            )})`,
            outFields,
            orderByFields,
            returnDistinctValues,
            exceedMaxRecordCount: false,
            ...rest
        });
        inds = removeDuplicateItems(
            (oIndicator: any) => oIndicator.ID,
            [...inds, ...indsOr]
        );
    }
    return inds;
};

export const getIndicatorsForTheme = async ({
    url,
    themeId,
    outFields = 'Data_Type, ID, Item_Order, Item_Type, Name, Short_Name, Theme_ID',
    orderByFields = 'Item_Order ASC',
    returnDistinctValues = true,
    ...rest
}: {
    url: string;
    themeId: string;
    outFields?: string;
    orderByFields?: string;
    returnDistinctValues?: boolean;
}): Promise<any[]> =>
    await getAttributes({
        url,
        where: `Item_Type='Indicator' AND Theme_ID='${themeId}'`,
        outFields,
        orderByFields,
        returnDistinctValues,
        ...rest
    });

export const getInstance = async ({
    url,
    id,
    outFields = 'Date_ID, Field_ID, Geo_ID, ID, Indicator_ID, Item_Order, Item_Type, Name, Service_Url, Short_Name',
    orderByFields = 'Item_Order ASC, Date_ID ASC ',
    returnDistinctValues = true,
    ...rest
}: {
    url: string;
    id: string;
    outFields?: string;
    orderByFields?: string;
    returnDistinctValues?: boolean;
}): Promise<any> => {
    const insts = await getAttributes({
        url,
        where: `Item_Type='Instance' AND ID='${id}'`,
        outFields,
        orderByFields,
        returnDistinctValues,
        ...rest
    });
    return insts[0];
};

export const getInstancesForIndicatorGeo = async ({
    url,
    indicatorId,
    geoId,
    outFields = 'Date_ID, Field_ID, Geo_ID, ID, Indicator_ID, Item_Order, Item_Type, Name, Service_Url, Short_Name',
    orderByFields = 'Item_Order ASC, Date_ID ASC ',
    returnDistinctValues = true,
    ...rest
}: {
    url: string;
    indicatorId: string;
    geoId: string;
    outFields?: string;
    orderByFields?: string;
    returnDistinctValues?: boolean;
}): Promise<any> =>
    await getAttributes({
        url,
        where: `Item_Type='Instance' AND Indicator_ID='${indicatorId}' AND Geo_ID='${geoId}'`,
        outFields,
        orderByFields,
        returnDistinctValues,
        ...rest
    });

export const getInstancesForIndicator = async ({
    url,
    indicatorId,
    outFields = 'Date_ID, Field_ID, Geo_ID, ID, Indicator_ID, Item_Order, Item_Type, Name, Service_Url, Short_Name',
    orderByFields = 'Item_Order ASC, Date_ID ASC ',
    returnDistinctValues = true,
    ...rest
}: {
    url: string;
    indicatorId: string;
    outFields?: string;
    orderByFields?: string;
    returnDistinctValues?: boolean;
}): Promise<any> =>
    await getAttributes({
        url,
        where: `Item_Type='Instance' AND Indicator_ID='${indicatorId}'`,
        outFields,
        orderByFields,
        returnDistinctValues,
        ...rest
    });

export const getRootThemes = async ({
    url,
    outFields = 'ID, Item_Order, Item_Type, Name, Short_Name, Theme_ID',
    orderByFields = 'Item_Order ASC',
    returnDistinctValues = true,
    ...rest
}: {
    url: string;
    outFields?: string;
    orderByFields?: string;
    returnDistinctValues?: boolean;
}) =>
    await getAttributes({
        url,
        where: `Item_Type='Theme' AND Theme_ID IS NULL`,
        outFields,
        orderByFields,
        returnDistinctValues,
        ...rest
    });

export const getTheme = async ({
    url,
    id,
    ...rest
}: {
    url: string;
    id: string;
}): Promise<any> => {
    const thms = await getThemes({ url, ids: [id], ...rest });
    return thms[0];
};

export const getThemes = async ({
    url,
    ids = [],
    outFields = 'ID, Item_Order, Item_Type, Name, Short_Name, Theme_ID',
    orderByFields = 'Item_Order ASC',
    returnDistinctValues = true,
    ...rest
}: {
    url: string;
    ids?: string[];
    outFields?: string;
    orderByFields?: string;
    returnDistinctValues?: boolean;
}): Promise<any> =>
    await getAttributes({
        url,
        where:
            ids.length > 0
                ? `Item_Type='Theme' AND ID IN ('${ids.join("','")}')`
                : `Item_Type='Theme'`,
        outFields,
        orderByFields,
        returnDistinctValues,
        ...rest
    });

export const getThemesAndIndicatorsForTheme = async ({
    url,
    themeId,
    outFields = 'Data_Type, Denominator, ID, Item_Order, Item_Type, Name, Numerator, Scale_Factor, Short_Name, Theme_ID',
    orderByFields = 'Item_Type DESC, Item_Order ASC',
    returnDistinctValues = true,
    ...rest
}: {
    url: string;
    themeId: string;
    outFields?: string;
    orderByFields?: string;
    returnDistinctValues?: boolean;
}): Promise<any> =>
    await getAttributes({
        url,
        where: `(Item_Type='Theme' AND Theme_ID='${themeId}') OR (Item_Type='Indicator' AND Theme_ID='${themeId}')`,
        outFields,
        orderByFields,
        returnDistinctValues,
        ...rest
    });

export const getThemesForTheme = async ({
    url,
    themeId,
    outFields = 'ID, Item_Order, Item_Type, Name, Short_Name, Theme_ID',
    orderByFields = 'Item_Order ASC',
    returnDistinctValues = true,
    ...rest
}: {
    url: string;
    themeId: string;
    outFields?: string;
    orderByFields?: string;
    returnDistinctValues?: boolean;
}): Promise<any> =>
    await getAttributes({
        url,
        where: `Item_Type='Theme' AND Theme_ID='${themeId}'`,
        outFields,
        orderByFields,
        returnDistinctValues,
        ...rest
    });

// Metadata
export const getMeta = async ({
    url,
    indicatorId,
    geoId,
    instanceId,
    outFields = '*',
    returnDistinctValues = true,
    ...rest
}: {
    url: string;
    indicatorId?: string;
    geoId?: string;
    instanceId?: string;
    outFields?: string;
    returnDistinctValues?: boolean;
}): Promise<any> => {
    const ind = indicatorId !== undefined ? `'${indicatorId}'` : 'null';
    const geo = geoId !== undefined ? `'${geoId}'` : 'null';
    const inst = instanceId !== undefined ? `'${instanceId}'` : 'null';
    const meta = await getAttributes({
        url,
        where: `IndicatorID=${ind} AND GeoID=${geo} AND InstanceID=${inst}`,
        outFields,
        returnDistinctValues,
        ...rest
    });
    return meta[0];
};
